/**
 * @file postedEditChanges.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-03
 * @modified 2026-10-08
 * @description What an edit to a posted invoice or credit note will change, in
 *              plain words, for the reason dialog to show before saving. The
 *              server works the same thing out for itself (and for the audit
 *              row); this is the courtesy of saying it first.
 *
 *              Lines are paired by the stored line they came from
 *              (`invoice_line_id`, utils/lineItems); a row with none is new.
 */

import { formatQuantity } from './numberFormatters';

const text = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (v) => num(v).toFixed(2);
/** A quantity in the words: 2dp, more when it has them ("2.00", "2.0125"). */
const qty = (v) => formatQuantity(num(v));
const sameNum = (a, b) => Math.abs(num(a) - num(b)) < 1e-6;
const sameCode = (a, b) => text(a).toUpperCase() === text(b).toUpperCase();
// The screen holds a missing tax type as null and sends it as NONE: the same.
const sameTax = (a, b) => sameCode(a || 'NONE', b || 'NONE');
const additiveKey = (list) => (Array.isArray(list) ? list : [])
  .map(a => `${a.additive_id ?? ''}|${text(a.additive_code).toUpperCase()}|${num(a.additive_price).toFixed(4)}`)
  .sort()
  .join(';');
const shortName = (l) => text(l.product_name || l.product_code || 'line').slice(0, 50);
const ukDate = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '(none)';
};

/**
 * @param {object} before  { invoiceDate, dueDate, reference, notes, lines }
 * @param {object} after   the same, as the screen now holds it
 * @returns {string[]} one sentence per change; empty when nothing changed
 */
export function describeInvoiceEdit(before, after) {
  const out = [];
  if (!before || !after) return out;
  if (text(before.invoiceDate).slice(0, 10) !== text(after.invoiceDate).slice(0, 10)) {
    out.push(`Date ${ukDate(before.invoiceDate)} → ${ukDate(after.invoiceDate)}`);
  }
  if (text(before.dueDate).slice(0, 10) !== text(after.dueDate).slice(0, 10)) {
    out.push(`Due date ${ukDate(before.dueDate)} → ${ukDate(after.dueDate)}`);
  }
  if (text(before.reference) !== text(after.reference)) {
    out.push(`Reference "${text(before.reference) || '(none)'}" → "${text(after.reference) || '(none)'}"`);
  }
  if (text(before.notes) !== text(after.notes)) out.push('Notes (kept here only)');

  const beforeById = new Map((before.lines || []).filter(l => l.invoice_line_id != null).map(l => [Number(l.invoice_line_id), l]));
  const seen = new Set();
  (after.lines || []).forEach((l, i) => {
    const id = l.invoice_line_id != null ? Number(l.invoice_line_id) : null;
    const b = id != null && beforeById.has(id) && !seen.has(id) ? beforeById.get(id) : null;
    const n = i + 1;
    if (!b) {
      out.push(`Line ${n} added: ${shortName(l)} × ${qty(l.quantity)} @ £${money(l.unit_price)}`);
      return;
    }
    seen.add(id);
    const bits = [];
    if (!sameCode(b.product_code, l.product_code)) bits.push(`item ${text(b.product_code) || '(none)'} → ${text(l.product_code) || '(none)'}`);
    if (text(b.product_name) !== text(l.product_name)) bits.push('description');
    if (!sameNum(b.quantity, l.quantity)) bits.push(`quantity ${qty(b.quantity)} → ${qty(l.quantity)}`);
    if (!sameNum(b.unit_price, l.unit_price)) bits.push(`price £${money(b.unit_price)} → £${money(l.unit_price)}`);
    if (!sameCode(b.account_code, l.account_code)) bits.push(`account ${text(b.account_code) || '(none)'} → ${text(l.account_code) || '(none)'}`);
    if (!sameTax(b.tax_type, l.tax_type)) bits.push(`tax ${text(b.tax_type) || 'none'} → ${text(l.tax_type) || 'none'}`);
    if (String(b.supplier_contact_id ?? '') !== String(l.supplier_contact_id ?? '')) bits.push('supplier (kept here only)');
    if (additiveKey(b.additives) !== additiveKey(l.additives)) bits.push('additives');
    if (bits.length) out.push(`Line ${n} (${shortName(l)}): ${bits.join(', ')}`);
  });
  for (const [id, b] of beforeById) {
    if (!seen.has(id)) out.push(`Removed: ${shortName(b)} × ${qty(b.quantity)} @ £${money(b.unit_price)}`);
  }
  return out;
}

export default describeInvoiceEdit;
